import { apiClient } from "./apiClient";
import { getAuthContext } from "./authContext";

export type SavedPrompt = {
  id: string;
  title: string;
  content: string;
  sourcePrompt?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
};

export type EnhancePromptResult = {
  title: string;
  enhancedPrompt: string;
  improvementNotes: string[];
  sourcePrompt: string;
  tokensUsed: number;
  modelUsed: string;
};

export const savedPromptsService = {
  async list(limit = 100): Promise<SavedPrompt[]> {
    const auth = getAuthContext();
    const response = await apiClient.get(`/${auth.tenantId}/saved-prompts`, {
      params: { limit },
    });
    const raw = response.data as {
      success?: boolean;
      data?: { prompts?: SavedPrompt[] };
      prompts?: SavedPrompt[];
    };
    const prompts = raw?.data?.prompts ?? raw?.prompts ?? [];
    return prompts.map((p: SavedPrompt) => ({
      id: String(p.id),
      title: p.title || "Untitled",
      content: p.content || "",
      sourcePrompt: p.sourcePrompt,
      metadata: p.metadata,
      createdAt: p.createdAt,
      updatedAt: p.updatedAt,
    }));
  },

  async create(payload: {
    title: string;
    content: string;
    sourcePrompt?: string;
    metadata?: Record<string, unknown>;
  }): Promise<void> {
    const auth = getAuthContext();
    await apiClient.post(`/${auth.tenantId}/saved-prompts`, payload);
  },

  async enhance(instruction: string): Promise<EnhancePromptResult> {
    const auth = getAuthContext();
    const response = await apiClient.post(`/${auth.tenantId}/saved-prompts/enhance`, {
      prompt: instruction,
    });
    const raw = response.data as {
      success?: boolean;
      data?: EnhancePromptResult;
    };
    const data = raw?.data;
    if (!data?.enhancedPrompt) {
      throw new Error("Could not generate prompt");
    }
    return data;
  },

  async delete(promptId: string): Promise<void> {
    const auth = getAuthContext();
    await apiClient.delete(`/${auth.tenantId}/saved-prompts/${promptId}`);
  },
};
